/**
 * TypeBadge — Displays the detected semantic type of panel content.
 *
 * Color-coded by type family (JSON=violet, CSV=emerald, Regex=amber, etc.).
 * Differentiates auto-detected vs manually selected type via border style:
 *   - Auto-detected: dashed border (ephemeral, system-chosen)
 *   - Manual: solid border (deliberate user selection)
 */

import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface TypeBadgeProps {
  /** The semantic type display name (e.g. "JSON", "CSV", "Markdown"). */
  type: string;
  /** Whether this type was auto-detected (true) or manually selected (false). */
  isAuto?: boolean;
  className?: string;
}

const TYPE_BADGE_STYLES: Record<string, string> = {
  json: 'text-violet-400 bg-violet-400/10',
  csv: 'text-emerald-400 bg-emerald-400/10',
  tsv: 'text-emerald-400 bg-emerald-400/10',
  regex: 'text-amber-400 bg-amber-400/10',
  xml: 'text-cyan-400 bg-cyan-400/10',
  yaml: 'text-cyan-400 bg-cyan-400/10',
  html: 'text-rose-400 bg-rose-400/10',
  markdown: 'text-blue-400 bg-blue-400/10',
  number: 'text-orange-400 bg-orange-400/10',
  hex: 'text-pink-400 bg-pink-400/10',
  base64: 'text-pink-400 bg-pink-400/10',
  jwt: 'text-amber-400 bg-amber-400/10',
  url: 'text-sky-400 bg-sky-400/10',
  uuid: 'text-indigo-400 bg-indigo-400/10',
};

function getTypeBadgeStyle(typeName: string): string {
  const key = typeName.toLowerCase();
  for (const [k, v] of Object.entries(TYPE_BADGE_STYLES)) {
    if (key.includes(k)) return v;
  }
  return 'text-muted-foreground bg-white/5';
}

export const TypeBadge: React.FC<TypeBadgeProps> = ({
  type,
  isAuto = true,
  className,
}) => {
  const tooltipText = isAuto
    ? `Auto-detected: ${type}`
    : `Manually selected: ${type}`;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0',
              'border transition-colors duration-150',
              getTypeBadgeStyle(type),
              isAuto ? 'border-dashed border-current/20' : 'border-solid border-current/30',
              className
            )}
            aria-label={tooltipText}
          >
            {type}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-[11px]">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};